import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Container, Section, SectionHeader } from './styles';
import TouristOffersBusinessService from 'services/TouristOffersBusinessService';
import TransportCategoryService from 'services/TransportCategoryService';
import { IBusiness } from 'interfaces/IBusinesses';
import CategoryTable from 'components/CategoryTable';
import Heading from 'components/Heading';
import Button from 'components/Button';
import ConfirmationModal from 'components/ConfirmationModal';
import eyeIcon from 'assets/images/eyeGrayIcon.svg';
import editIcon from 'assets/images/editIcon.svg';
import trashIcon from 'assets/images/trashIcon.svg';

import { DataGrid, GridColDef, GridToolbar, GridColumnVisibilityModel } from '@mui/x-data-grid';
import { Grid } from '@mui/material';

const Transport = (): JSX.Element => {
  const [transports, setTransports] = useState<IBusiness[]>([]);
  const [transportId, setTransportId] = useState(0);
  const [confirmDeletion, setConfirmDeletion] = useState(false);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>({
    id: false,
  });

  const history = useHistory();
  const businessService = new TouristOffersBusinessService();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const filteredTransports = await businessService.findAllSeturTransports();
      setTransports(filteredTransports);
    } catch (error) {
      console.error(error);
    }
  };

  const deleteTransport = async () => {
    try {
      await businessService.remove(transportId);
      setTransports(prev => prev.filter(item => item.id !== transportId));
    } catch (error) {
      console.error(error);
    }
  };

  const formatEventCreate = (dateString: string): JSX.Element => {
    if (!dateString) return <span>- - -</span>;

    const date = new Date(dateString);
    const formattedDate = date.toLocaleDateString('pt-BR');
    const formattedTime = date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });

    return (
      <div className="createdDate">
        <span>{formattedDate}</span>
        <span>{formattedTime}</span>
      </div>
    );
  };

  const transportColumns: GridColDef[] = [
    {
      field: 'id',
      headerName: 'ID',
      flex: 0.5,
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'createdAt',
      headerName: 'Criação',
      flex: 2,
      minWidth: 200,
      align: 'center',
      headerAlign: 'center',
      renderCell: params => formatEventCreate(params.value as string),
    },
    {
      field: 'updatedAt',
      headerName: 'Última Edição',
      flex: 2,
      minWidth: 200,
      align: 'center',
      headerAlign: 'center',
      renderCell: params => formatEventCreate(params.value as string),
    },
    {
      field: 'user',
      headerName: 'Cadastrado/Editado Por',
      flex: 2,
      minWidth: 200,
      align: 'center',
      headerAlign: 'center',
      valueGetter: params => params.row.user?.name || params.row.user?.email || '- - -',
    },
    {
      field: 'title',
      headerName: 'Título do Transporte',
      flex: 2,
      minWidth: 200,
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'tradeName',
      headerName: 'Nome Fantasia',
      flex: 2,
      minWidth: 200,
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'document',
      headerName: 'CNPJ',
      flex: 1.5,
      minWidth: 180,
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'phone',
      headerName: 'Telefone',
      flex: 1.5,
      minWidth: 150,
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'businessType',
      headerName: 'Classificação',
      flex: 2,
      minWidth: 180,
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Ações',
      flex: 1,
      minWidth: 180,
      align: 'center',
      headerAlign: 'center',
      renderCell: params => (
        <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', width: '100%' }}>
          <button
            className="button-icon"
            title="Visualizar"
            onClick={() => history.push(`/admin/business-list-overview/id=${params.id}`)}>
            <img src={eyeIcon} alt="visualizar" />
          </button>

          <button className="button-icon" title="Editar" onClick={() => history.push(`/admin/transports/${params.id}`)}>
            <img src={editIcon} alt="editar" />
          </button>

          <button
            className="button-icon"
            title="Excluir"
            onClick={() => {
              setTransportId(Number(params.id));
              setConfirmDeletion(true);
            }}>
            <img src={trashIcon} alt="excluir" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <Container>
      <CategoryTable title="Categorias de Transportes" service={new TransportCategoryService()} />
      <Section>
        <SectionHeader>
          <Heading>Lista de Transportes Cadastrados pela SETUR</Heading>
          <Button type="button" buttonType="primary" onClick={() => history.push('/admin/transports/new-transport')}>
            Adicionar
          </Button>
        </SectionHeader>

        <Grid sx={{ width: '100%', marginBottom: '50px' }}>
          <DataGrid
            sx={{
              background: '#fff',
              boxShadow: '0px 0px 10px 0px rgba(0,0,0,0.1)',
              borderRadius: '18px',
              width: '100%',
              border: 'none',
            }}
            rows={transports}
            columns={transportColumns}
            initialState={{
              pagination: { paginationModel: { pageSize: 10 } },
            }}
            pageSizeOptions={[10, 25]}
            columnVisibilityModel={columnVisibilityModel}
            onColumnVisibilityModelChange={newModel => setColumnVisibilityModel(newModel)}
            slots={{ toolbar: GridToolbar }}
            slotProps={{
              toolbar: {
                showQuickFilter: true,
                densitySelector: false,
                exportButton: false,
              },
            }}
            disableRowSelectionOnClick
            autoHeight
          />
        </Grid>
      </Section>

      <ConfirmationModal
        open={confirmDeletion}
        title="EXCLUSÃO DE ITEM"
        message="Você tem certeza de que deseja excluir esse item?"
        onCancel={() => setConfirmDeletion(false)}
        onConfirm={() => {
          setConfirmDeletion(false);
          deleteTransport();
        }}
      />
    </Container>
  );
};

export default Transport;
